import { useState } from 'react';
import { formatManilaDate, formatInclusiveManilaPeriodEnd } from '@dispatchdash/shared';
import { commissionApi, type CommissionSettlementRun, type CommissionSettlementRunDetail } from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { salesNavItems } from '@/pages/navItems';
import { formatPeso, humanize } from '@/lib/format';

export function SalesSettlements() {
  const { data: runs, loading, error, reload } = useAsync(() => commissionApi.listSettlementRuns(), []);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CommissionSettlementRunDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const loadDetail = async (id: string) => {
    setDetailId(id);
    setDetailLoading(true);
    setDetailError('');
    try {
      setDetail(await commissionApi.getSettlementRun(id));
    } catch (err) {
      setDetailError((err as { message?: string }).message ?? 'This settlement couldn\'t be loaded. Please try again.');
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    {
      key: 'periodStart',
      header: 'Period',
      render: (row: CommissionSettlementRun) =>
        row.period ??
        `${formatManilaDate(row.periodStart)} → ${formatInclusiveManilaPeriodEnd(row.periodEnd, row.periodStart)}`,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row: CommissionSettlementRun) => <StatusBadge status={row.status} size="sm" />,
    },
    { key: 'commissionTotal', header: 'Commission', align: 'right' as const, render: (row: CommissionSettlementRun) => formatPeso(row.commissionTotal) },
    { key: 'bonusTotal', header: 'Bonus', align: 'right' as const, render: (row: CommissionSettlementRun) => formatPeso(row.bonusTotal) },
    { key: 'netPayable', header: 'Net payable', align: 'right' as const, render: (row: CommissionSettlementRun) => formatPeso(row.netPayable) },
    {
      key: 'actions',
      header: '',
      sortable: false,
      render: (row: CommissionSettlementRun) => (
        <Button size="sm" variant="secondary" type="button" onClick={() => loadDetail(row.id)}>View</Button>
      ),
    },
  ];

  return (
    <Layout navItems={salesNavItems}>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>My settlements</h1>
            <p className="text-muted">Your commission payouts by period</p>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={runs ?? []}
          keyField="id"
          loading={loading}
          dense
          loadError={error ? `Settlements couldn't be loaded. ${error}` : null}
          onRetry={() => void reload()}
          searchPlaceholder="Filter settlements…"
          emptyMessage="No settlements yet. Commission is settled at the end of each period."
        />

        <Modal
          open={detailId !== null}
          onClose={() => { setDetailId(null); setDetail(null); }}
          title="Settlement details"
          size="lg"
        >
          {detailLoading && <p className="text-muted">Loading…</p>}
          {detailError && <p className="form-error" style={{ marginBottom: 'var(--space-md)' }}>{detailError}</p>}
          {detail && !detailLoading && (
            <div>
              <div className="grid-3" style={{ marginBottom: 'var(--space-lg)' }}>
                <div className="stat-block">
                  <span className="stat-value">{formatPeso(detail.run.commissionTotal)}</span>
                  <span className="stat-label">Commission</span>
                </div>
                <div className="stat-block">
                  <span className="stat-value">{formatPeso(detail.run.bonusTotal)}</span>
                  <span className="stat-label">Bonus</span>
                </div>
                <div className="stat-block">
                  <span className="stat-value">{formatPeso(detail.run.netPayable)}</span>
                  <span className="stat-label">Net payable</span>
                </div>
              </div>
              <h3 style={{ marginBottom: 'var(--space-sm)' }}>Trips in this settlement ({detail.entries.length})</h3>
              {detail.entries.map((entry) => (
                <div key={entry.id} className="frozen-line-row">
                  <span>
                    <strong>{entry.tripNumber ?? 'Trip'}</strong>
                    {entry.clientName ? ` · ${entry.clientName}` : ''}
                    <span className="frozen-line-sub">
                      Revenue {formatPeso(entry.tripRevenue)} · Gross profit {formatPeso(entry.grossProfit)}
                      {entry.state ? <> · {humanize(entry.state)}</> : null}
                    </span>
                  </span>
                  <span>{formatPeso(entry.amount)}</span>
                </div>
              ))}
              {detail.bonusAwards.length > 0 && (
                <>
                  <h3 style={{ margin: 'var(--space-lg) 0 var(--space-sm)' }}>Bonuses</h3>
                  {detail.bonusAwards.map((award) => (
                    <div key={award.id} className="frozen-line-row">
                      <span>{award.ruleLabel}</span>
                      <span>{formatPeso(award.amount)}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </Modal>
      </div>
      <style>{`
        .frozen-line-row {
          display: flex;
          justify-content: space-between;
          padding: var(--space-sm) 0;
          border-bottom: 1px solid var(--color-border);
          font-size: 0.875rem;
          gap: var(--space-md);
        }
        .frozen-line-sub {
          display: block;
          color: var(--color-text-muted);
          font-size: 0.8125rem;
        }
        .form-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          font-size: 0.875rem;
        }
      `}</style>
    </Layout>
  );
}
